import { useMemo } from "react";
import { Link, useSearchParams } from "react-router";
import { useTranslate } from "ra-core";
import { Info } from "lucide-react";

import { Button } from "@/components/ui/button";

import { VoltDataTable } from "../ui/VoltDataTable";
import { VoltFigures } from "../ui/VoltFigures";
import { VoltFilterBar } from "../ui/VoltFilterBar";
import { VoltPageHeader } from "../ui/VoltPageHeader";
import { VoltSortMenu } from "../ui/VoltSortMenu";
import { VoltYearTabs } from "../ui/VoltYearTabs";
import { withVoltParam } from "../ui/voltFilters";
import { WorkshopError } from "../workshop/WorkshopError";
import { WorkshopTabs } from "../workshop/WorkshopTabs";
import {
  eur,
  FUNDING_SORTS,
  hasRanking,
  placeOf,
  sortedRows,
  type FundingSort,
} from "./fundingText";
import type { FundingRow } from "./types";
import {
  DIR_PARAM,
  listLink,
  SEARCH_PARAM,
  SORT_PARAM,
  YEAR_PARAM,
  yearOf,
} from "./fundingLinks";
import { FundingDenied, FundingLoading } from "./FundingStates";
import { useFundingList } from "./useFundingList";

export const FundingListPage = () => {
  const translate = useTranslate();
  const [params, setParams] = useSearchParams();
  const { data, error, denied } = useFundingList(yearOf(params));

  const set = (name: string, value: string | null) =>
    setParams((prev) => withVoltParam(prev, name, value), { replace: true });

  const rows = useMemo(() => data?.zeilen ?? [], [data]);
  const ranked = hasRanking(rows);
  const by: FundingSort =
    params.get(SORT_PARAM) === "ranking" && ranked ? "ranking" : "number";
  const dir = params.get(DIR_PARAM) === "desc" ? "desc" : "asc";
  const query = (params.get(SEARCH_PARAM) ?? "").trim().toLowerCase();

  const visible = useMemo(
    () =>
      sortedRows(rows, by, dir).filter(
        (r) =>
          !query ||
          `${r.nr} ${r.traeger} ${r.titel}`.toLowerCase().includes(query),
      ),
    [rows, by, dir, query],
  );

  if (denied !== null) return <FundingDenied reason={denied} />;
  if (!data) {
    if (error) return <WorkshopError>{error}</WorkshopError>;
    return <FundingLoading />;
  }

  const year = data.liste.foerderjahr;
  const requested = rows.reduce((s, r) => s + r.beantragt, 0);

  return (
    <div className="flex flex-col gap-4">
      <WorkshopTabs />
      <VoltPageHeader
        className="mb-0"
        title={translate("crm.funding_list.menu", { _: "Quartiersfonds" })}
        description="Vertraulich"
      />
      {error ? <WorkshopError>{error}</WorkshopError> : null}
      <VoltYearTabs
        years={data.jahre?.length ? data.jahre : [year]}
        value={year}
        label="Förderjahr"
        onChange={(y) => set(YEAR_PARAM, String(y))}
      />
      <VoltFigures
        items={[
          { label: "Anträge", value: rows.length },
          { label: "beantragt gesamt", value: eur(requested) },
          { label: "Fondsvolumen", value: eur(data.liste.fondsvolumen) },
        ]}
      />
      <div className="flex flex-wrap items-start justify-between gap-2">
        <VoltFilterBar
          filters={[]}
          searchParam={SEARCH_PARAM}
          searchPlaceholder="Suche Projekt oder Träger"
          className="mb-0 flex-1"
        />
        {ranked ? (
          <VoltSortMenu
            options={FUNDING_SORTS}
            value={by}
            onChange={(v) => set(SORT_PARAM, v === "number" ? null : v)}
            direction={dir}
            onDirectionChange={(d) => set(DIR_PARAM, d === "asc" ? null : d)}
          >
            <Button
              variant="ghost"
              size="icon"
              aria-label="So entsteht das Ranking"
              title="So entsteht das Ranking"
              asChild
            >
              <Link to={listLink("/funding-list/ranking", params, year)}>
                <Info />
              </Link>
            </Button>
          </VoltSortMenu>
        ) : null}
      </div>
      <VoltDataTable<FundingRow>
        rows={visible}
        rowKey={(r) => r.antragId}
        rowHref={(r) => listLink(`/funding-list/${r.antragId}`, params, year)}
        empty={
          <p className="text-sm text-muted-foreground">
            Keine Anträge für diese Suche.
          </p>
        }
        columns={[
          { key: "nr", header: "Nr.", cell: (r) => r.nr },
          { key: "titel", header: "Projekt", cell: (r) => r.titel ?? "–" },
          { key: "traeger", header: "Träger", cell: (r) => r.traeger },
          {
            key: "ort",
            header: "Stadtteil",
            cell: (r) => placeOf(r),
            hideOnMobile: true,
          },
          {
            key: "beantragt",
            header: "beantragt",
            align: "right",
            cell: (r) => eur(r.beantragt),
          },
        ]}
      />
    </div>
  );
};
